import './pay_period.css'

import { gridViewManager } from '@/utils/grid'
import { LeftOutlined } from '@ant-design/icons'
import { useFormatter } from '@api/useFormatter'
import { usePayroll } from '@api/usePayroll'
import { PayPeriodDetail } from '@components/DriverManagement/PayPeriod/Detail/PayPeriodDetail'
import { OrderModal } from '@components/OrderWizard/OrderModal'
import { ExpandCollapseButtons } from '@components/_SiteWide/ExpandCollapseButtons'
import { DateSkipper, GraviGrid, Horizontal, Texto, addCommasToNumber } from '@gravitate-js/excalibrr'
import { useUserSettings } from '@hooks/settings'
import { useQueryClient } from '@tanstack/react-query'
import { formatters } from '@utils/intl/dateTimeFormatters'
import moment from 'moment'
import { useMemo, useRef, useState } from 'react'

const STORAGE_KEY = 'pay-period-details'

export function PayPeriodDetails({ driver, onWeekChange, weekCount, controlFilters, setSelectedDriver }) {
  const { usePayPeriodDetailsQuery } = usePayroll()
  const { isFetching, data: payPeriodData } = usePayPeriodDetailsQuery(controlFilters, driver.driver_id)
  const [selectedOrder, setSelectedOrder] = useState()
  const [showingModal, setShowingModal] = useState(false)
  const [isExpanded, setExpanded] = useState(false)
  const gridAPIRef = useRef()
  const queryClient = useQueryClient()
  const { updateSetting } = useUserSettings()

  const { formatCurrency } = useFormatter()
  const agPropOverrides = useMemo(() => ({ rowGroupPanelShow: 'never', getRowId: (row) => row?.data?.id }), [])
  const columnDefs = useMemo(() => getColumnDefs(formatCurrency), [formatCurrency])

  return (
    <div className='pay-period-container h-100 pb-3'>
      <Texto category='h5' weight='bold' className='mb-3'>
        <a
          onClick={() => {
            queryClient.invalidateQueries({
              queryKey: ['payrollOverviewQuery'],
            })
            setSelectedDriver()
          }}
        >
          <LeftOutlined className='pr-2' /> Back To All Drivers
        </a>
      </Texto>
      <OrderModal
        showingModal={showingModal}
        closeModal={() => {
          setSelectedOrder(null)
          setShowingModal(false)
        }}
        selectedOrder={selectedOrder}
      />
      <GraviGrid
        loading={isFetching}
        rowData={payPeriodData?.rows}
        externalRef={gridAPIRef}
        controlBarProps={{
          hiddenFilterKeys: ['date'],
          title: `Period Details for ${driver.driver_name}`,
          actionButtons: (
            <>
              <ExpandCollapseButtons gridAPIRef={gridAPIRef} setExpanded={setExpanded} isExpanded={isExpanded} />

              <DateSkipper
                startDate={controlFilters?.date}
                onChange={onWeekChange}
                format={formatters.DATE}
                daysToSkip={weekCount * 7}
              />
            </>
          ),
        }}
        masterDetail
        detailCellRenderer={PayPeriodDetail}
        detailRowAutoHeight
        detailCellRendererParams={{
          driver_id: driver.driver_id,
          setSelectedOrder,
          setShowingModal,
        }}
        storageKey={STORAGE_KEY}
        agPropOverrides={agPropOverrides}
        columnDefs={columnDefs}
        onRowGroupOpened={(params) => {
          if (params.node.expanded) {
            setExpanded(true)
          } else {
            let x = false
            params.api.forEachNodeAfterFilter((node) => {
              if (node.expanded) x = true
            })
            setExpanded(x)
          }
        }}
        onGridConfigChanged={(config) => updateSetting(`gridConfig::${STORAGE_KEY}`, config)}
        {...gridViewManager(STORAGE_KEY, updateSetting)}
      />
    </div>
  )
}

const getColumnDefs = (formatCurrency) => {
  const formattedCurrency = formatCurrency(null, true)

  return [
    {
      cellRenderer: 'agGroupCellRenderer',
      field: 'id',
      valueFormatter: () => '',
      headerName: '',
      maxWidth: 50,
    },
    {
      field: 'date',
      cellRenderer: ({ value }) => (
        <Texto category='h4' weight='bold'>
          {formatters.DATE(moment(value))}
        </Texto>
      ),
    },
    { headerName: 'Automatic Pay Config', field: 'payroll_config' },
    {
      field: 'pay_total',
      headerName: 'Daily Pay Total',
      filter: 'agNumberColumnFilter',
      valueGetter: ({ data }) => {
        const correctValue = data.override_pay_total || data.pay_total
        return correctValue?.toFixed ? parseFloat(correctValue?.toFixed(2)) : 0
      },
      valueFormatter: ({ value }) => addCommasToNumber(value, 2),
      type: 'rightAligned',
      cellRenderer: ({ value, data }) => {
        const total = value
        const hoursObj = data.shift_pay?.pay_items.find((item) => item.name === 'Hours')
        const getPayPerhr = () => {
          const hours = hoursObj.override_quantity || hoursObj.quantity
          if (hours > 0) {
            const payPerhr = value / hours
            return addCommasToNumber(payPerhr, 2)
          }
          return addCommasToNumber(value, 2) || '0.00'
        }

        return (
          <Horizontal justifyContent='flex-end' verticalCenter>
            {hoursObj && (
              <Texto appearance='hint' category='label' weight={600}>
                {formattedCurrency}
                {getPayPerhr()}/hr
              </Texto>
            )}
            <Texto
              weight='bold'
              className='pl-3'
              style={{ width: 120 }}
              align='right'
              appearance='success'
              category='h6'
            >
              {formattedCurrency}
              {addCommasToNumber(total, 2)}
            </Texto>
          </Horizontal>
        )
      },
    },
  ]
}
